import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Zap, Check, Loader2, Package, Shield, ShoppingCart, Globe,
  Code, Brain, Smartphone, Server
} from "lucide-react";
import { toast } from "sonner";

const PACK_ICONS = {
  "saas-starter": Package,
  "mobile-app": Smartphone,
  "api-backend": Server,
  "marketing-website": Globe,
  "webapp-essentials": Code,
  "ecommerce": ShoppingCart,
  "auth-security": Shield,
  "ai-integration": Brain,
};

export default function OnboardingPage() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [installing, setInstalling] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    try {
      const { data } = await API.get("/library/packs");
      setPacks(data);
    } catch (e) {
      console.error("Failed to load packs:", e);
    } finally {
      setLoading(false);
    }
  };

  const togglePack = (packId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(packId)) next.delete(packId);
      else next.add(packId);
      return next;
    });
  };

  const handleInstall = async () => {
    if (selected.size === 0) return;
    setInstalling(true);
    let installed = 0;
    for (const packId of selected) {
      try {
        await API.post(`/library/packs/${packId}/install`);
        installed++;
      } catch (e) {
        console.error(`Failed to install ${packId}:`, e);
      }
    }
    toast.success(`Installed ${installed} pack${installed !== 1 ? "s" : ""}`, {
      description: "Your library is ready. Let's build something.",
    });
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)] flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <div className="border-b border-[var(--pf-border)] px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          <div className="w-6 h-6 bg-[var(--pf-accent)] flex items-center justify-center">
            <Zap className="w-3 h-3 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-base font-bold tracking-tight text-[var(--pf-text)]">staqblox</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-[var(--pf-text)] mb-2" data-testid="onboarding-title">What are you building?</h1>
            <p className="text-sm text-[var(--pf-text-secondary)] max-w-xl mx-auto">
              Pick the packs that match your playbook. Each one pre-loads your library with production-grade prompt blocks across all 5 development phases.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--pf-accent)]" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="pack-grid">
                {packs.map((pack) => {
                  const isSelected = selected.has(pack.id);
                  const Icon = PACK_ICONS[pack.id] || Package;
                  return (
                    <button
                      key={pack.id}
                      onClick={() => togglePack(pack.id)}
                      disabled={pack.installed}
                      data-testid={`onboard-pack-${pack.id}`}
                      className={`text-left border p-4 transition-all duration-150 ${
                        pack.installed
                          ? "border-[var(--pf-border)] opacity-50 cursor-not-allowed"
                          : isSelected
                          ? "border-[var(--pf-accent)] bg-[var(--pf-accent)]/5 ring-2 ring-[var(--pf-accent)]"
                          : "border-[var(--pf-border)] hover:border-[var(--pf-accent)] cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Icon className={`w-5 h-5 ${isSelected ? "text-[var(--pf-accent)]" : "text-[var(--pf-text-muted)]"}`} />
                        {pack.installed ? (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium">Installed</span>
                        ) : isSelected ? (
                          <Check className="w-4 h-4 text-[var(--pf-accent)]" />
                        ) : null}
                      </div>
                      <h3 className="text-sm font-semibold text-[var(--pf-text)] mb-1">{pack.name}</h3>
                      <p className="text-[11px] text-[var(--pf-text-muted)] leading-relaxed line-clamp-2">{pack.description}</p>
                      <p className="text-[10px] text-[var(--pf-text-muted)] mt-2">{pack.block_count} blocks</p>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={handleInstall}
                  disabled={selected.size === 0 || installing}
                  data-testid="onboarding-install"
                  className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-11 px-8 text-sm font-medium"
                >
                  {installing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Installing {selected.size} pack{selected.size !== 1 ? "s" : ""}...</>
                  ) : selected.size > 0 ? (
                    <>Install {selected.size} Pack{selected.size !== 1 ? "s" : ""} & Continue</>
                  ) : (
                    "Select at least one pack"
                  )}
                </Button>
                <button
                  onClick={() => navigate("/dashboard")}
                  data-testid="onboarding-skip"
                  className="text-xs text-[var(--pf-text-muted)] hover:text-[var(--pf-text-secondary)] font-medium"
                >
                  Skip for now
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
